import { ScadFeature } from './scad-feature-types';
import {
  CubeDiagram,
  SphereDiagram,
  CylinderDiagram,
  CircleDiagram,
  SquareDiagram,
  TranslateDiagram,
  RotateDiagram,
  ScaleDiagram,
  MirrorDiagram,
  UnionDiagram,
  DifferenceDiagram,
  IntersectionDiagram,
  HullDiagram,
  MinkowskiDiagram,
  LinearExtrudeDiagram,
  RotateExtrudeDiagram,
  Clone1dDiagram,
  Clone2dDiagram,
  Clone3dDiagram,
  FnDiagram,
} from '../components/code/diagrams';

export const PRIMITIVES: Record<string, ScadFeature> = {
  cube: {
    id: 'cube',
    label: 'Cube',
    code: 'cube([10, 10, 10], center=true);',
    description: 'Creates a 3D cube or rectangular prism.',
    fields: [
      {
        name: 'size',
        label: 'Size (X,Y,Z)',
        type: 'vector3',
        default: [10, 10, 10],
      },
      { name: 'center', label: 'Center', type: 'boolean', default: true },
    ],
    generate: (v) => `cube([${v.size.join(', ')}], center=${v.center});`,
    diagram: CubeDiagram,
  },
  sphere: {
    id: 'sphere',
    label: 'Sphere',
    code: 'sphere(r=5, $fn=30);',
    description: 'Creates a 3D sphere.',
    fields: [
      { name: 'r', label: 'Radius', type: 'number', default: 5 },
      { name: 'fn', label: 'Resolution ($fn)', type: 'number', default: 30 },
    ],
    generate: (v) => `sphere(r=${v.r}, $fn=${v.fn});`,
    diagram: SphereDiagram,
  },
  cylinder: {
    id: 'cylinder',
    label: 'Cylinder',
    code: 'cylinder(h=10, r=5, center=true, $fn=30);',
    description: 'Creates a 3D cylinder or cone.',
    fields: [
      { name: 'h', label: 'Height', type: 'number', default: 10 },
      { name: 'r1', label: 'Radius 1', type: 'number', default: 5 },
      { name: 'r2', label: 'Radius 2', type: 'number', default: 5 },
      { name: 'center', label: 'Center', type: 'boolean', default: true },
      { name: 'fn', label: 'Resolution ($fn)', type: 'number', default: 30 },
    ],
    generate: (v) =>
      `cylinder(h=${v.h}, r1=${v.r1}, r2=${v.r2}, center=${v.center}, $fn=${v.fn});`,
    diagram: CylinderDiagram,
  },
  circle: {
    id: 'circle',
    label: 'Circle',
    code: 'circle(r=5, $fn=30);',
    description: 'Creates a 2D circle.',
    fields: [
      { name: 'r', label: 'Radius', type: 'number', default: 5 },
      { name: 'fn', label: 'Resolution ($fn)', type: 'number', default: 30 },
    ],
    generate: (v) => `circle(r=${v.r}, $fn=${v.fn});`,
    diagram: CircleDiagram,
  },
  square: {
    id: 'square',
    label: 'Square',
    code: 'square([10, 10], center=true);',
    description: 'Creates a 2D square or rectangle.',
    fields: [
      { name: 'width', label: 'Width', type: 'number', default: 10 },
      { name: 'height', label: 'Height', type: 'number', default: 10 },
      { name: 'center', label: 'Center', type: 'boolean', default: true },
    ],
    generate: (v) => `square([${v.width}, ${v.height}], center=${v.center});`,
    diagram: SquareDiagram,
  },
  text: {
    id: 'text',
    label: 'Text',
    description: 'Creates 2D text.',
    code: 'text("Hello", size=10, font="Liberation Sans");',
    fields: [
      { name: 't', label: 'Text', type: 'string', default: 'Hello' },
      { name: 'size', label: 'Size', type: 'number', default: 10 },
      {
        name: 'font',
        label: 'Font',
        type: 'string',
        default: 'Liberation Sans',
      },
    ],
    generate: (v) => `text("${v.t}", size=${v.size}, font="${v.font}");`,
  },
};

export const TRANSFORMS: Record<string, ScadFeature> = {
  translate: {
    id: 'translate',
    label: 'Translate',
    before: 'translate([0, 0, 0]) ',
    after: '',
    wrap: true,
    description: 'Moves children along the X, Y, and Z axes.',
    fields: [
      {
        name: 'v',
        label: 'Vector (X,Y,Z)',
        type: 'vector3',
        default: [10, 0, 0],
      },
    ],
    generateWrap: (v) => ({
      before: `translate([${v.v.join(', ')}])\n`,
      after: '',
    }),
    diagram: TranslateDiagram,
  },
  rotate: {
    id: 'rotate',
    label: 'Rotate',
    before: 'rotate([0, 0, 0]) ',
    after: '',
    wrap: true,
    description:
      'Rotates children by the given degrees about the X, Y, and Z axes.',
    fields: [
      {
        name: 'a',
        label: 'Angle (X,Y,Z)',
        type: 'vector3',
        default: [90, 0, 0],
      },
    ],
    generateWrap: (v) => ({
      before: `rotate([${v.a.join(', ')}])\n`,
      after: '',
    }),
    diagram: RotateDiagram,
  },
  scale: {
    id: 'scale',
    label: 'Scale',
    before: 'scale([1, 1, 1]) ',
    after: '',
    wrap: true,
    description: 'Scales children along the X, Y, and Z axes.',
    fields: [
      {
        name: 'v',
        label: 'Scale (X,Y,Z)',
        type: 'vector3',
        default: [2, 2, 2],
        step: 0.1,
      },
    ],
    generateWrap: (v) => ({
      before: `scale([${v.v.join(', ')}])\n`,
      after: '',
    }),
    diagram: ScaleDiagram,
  },
  resize: {
    id: 'resize',
    label: 'Resize',
    before: 'resize([10, 10, 10]) ',
    after: '',
    wrap: true,
    description: 'Resizes children to match the given dimensions.',
    fields: [
      {
        name: 'newsize',
        label: 'Size (X,Y,Z)',
        type: 'vector3',
        default: [10, 10, 10],
      },
    ],
    generateWrap: (v) => ({
      before: `resize([${v.newsize.join(', ')}])\n`,
      after: '',
    }),
  },
  mirror: {
    id: 'mirror',
    label: 'Mirror',
    before: 'mirror([1, 0, 0]) ',
    after: '',
    wrap: true,
    description:
      'Mirrors children across a plane whose normal is the given vector.',
    fields: [
      {
        name: 'v',
        label: 'Normal (X,Y,Z)',
        type: 'vector3',
        default: [1, 0, 0],
      },
    ],
    generateWrap: (v) => ({
      before: `mirror([${v.v.join(', ')}])\n`,
      after: '',
    }),
    diagram: MirrorDiagram,
  },
  color: {
    id: 'color',
    label: 'Color',
    before: 'color([1, 0.5, 0]) ',
    after: '',
    wrap: true,
    description: 'Applies a color to children elements.',
    fields: [
      {
        name: 'c',
        label: 'Color (R,G,B,A)',
        type: 'vector4',
        default: [1, 0, 0, 1],
        step: 0.1,
      },
    ],
    generateWrap: (v) => ({
      before: `color([${v.c.join(', ')}])\n`,
      after: '',
    }),
  },
  material: {
    id: 'material',
    label: 'Material',
    before: 'material(material_def(metalness=0.5, roughness=0.5)) ',
    after: '',
    wrap: true,
    description: 'Applies a PBR material. Extended feature for this viewer.',
    fields: [
      {
        name: 'metalness',
        label: 'Metalness (0-1)',
        type: 'number',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.1,
      },
      {
        name: 'roughness',
        label: 'Roughness (0-1)',
        type: 'number',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.1,
      },
    ],
    generateWrap: (v) => ({
      before: `material(material_def(metalness=${v.metalness}, roughness=${v.roughness}))\n`,
      after: '',
    }),
  },
};

export const BOOLEAN_OPS: Record<string, ScadFeature> = {
  union: {
    id: 'union',
    label: 'Union',
    description: 'Combines all children into a single merged shape.',
    before: 'union() {\n  ',
    after: '\n}',
    wrap: true,
    diagram: UnionDiagram,
  },
  difference: {
    id: 'difference',
    label: 'Difference',
    description: 'Subtracts all subsequent children from the first child.',
    before: 'difference() {\n  ',
    after: '\n}',
    wrap: true,
    diagram: DifferenceDiagram,
  },
  intersection: {
    id: 'intersection',
    label: 'Intersection',
    description: 'Keeps only the volume shared by all children.',
    before: 'intersection() {\n  ',
    after: '\n}',
    wrap: true,
    diagram: IntersectionDiagram,
  },
};

export const ADVANCED_OPS: Record<string, ScadFeature> = {
  hull: {
    id: 'hull',
    label: 'Hull',
    description: 'Creates a convex hull around its child elements.',
    before: 'hull() {\n  ',
    after: '\n}',
    wrap: true,
    diagram: HullDiagram,
  },
  minkowski: {
    id: 'minkowski',
    label: 'Minkowski',
    description: 'Calculates the Minkowski sum of child elements.',
    before: 'minkowski() {\n  ',
    after: '\n}',
    wrap: true,
    diagram: MinkowskiDiagram,
  },
};

export const LIGHTS: Record<string, ScadFeature> = {
  ambient: {
    id: 'ambient',
    label: 'Ambient Light',
    code: 'light_ambient(color=[1,1,1], intensity=0.5);',
    description: 'Adds an ambient light to the scene.',
    fields: [
      {
        name: 'color',
        label: 'Color (R,G,B)',
        type: 'vector3',
        default: [1, 1, 1],
        step: 0.1,
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'number',
        default: 0.5,
        step: 0.1,
      },
    ],
    generate: (v) =>
      `light_ambient(color=[${v.color.join(', ')}], intensity=${v.intensity});`,
  },
  directional: {
    id: 'directional',
    label: 'Directional Light',
    code: 'light_directional(color=[1,1,1], position=[10,10,10], intensity=1.0);',
    description: 'Adds a directional light pointing towards the origin.',
    fields: [
      {
        name: 'color',
        label: 'Color (R,G,B)',
        type: 'vector3',
        default: [1, 1, 1],
        step: 0.1,
      },
      {
        name: 'position',
        label: 'Position (X,Y,Z)',
        type: 'vector3',
        default: [10, 10, 10],
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'number',
        default: 1.0,
        step: 0.1,
      },
    ],
    generate: (v) =>
      `light_directional(color=[${v.color.join(', ')}], position=[${v.position.join(', ')}], intensity=${v.intensity});`,
  },
  point: {
    id: 'point',
    label: 'Point Light',
    code: 'light_point(color=[1,1,1], position=[0,0,10], intensity=1.0, distance=50, decay=2);',
    description: 'Adds a point light radiating from a position.',
    fields: [
      {
        name: 'color',
        label: 'Color (R,G,B)',
        type: 'vector3',
        default: [1, 1, 1],
        step: 0.1,
      },
      {
        name: 'position',
        label: 'Position (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 10],
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'number',
        default: 1.0,
        step: 0.1,
      },
      { name: 'distance', label: 'Distance', type: 'number', default: 50 },
    ],
    generate: (v) =>
      `light_point(color=[${v.color.join(', ')}], position=[${v.position.join(', ')}], intensity=${v.intensity}, distance=${v.distance});`,
  },
  spot: {
    id: 'spot',
    label: 'Spot Light',
    code: 'light_spot(color=[1,1,1], position=[0,0,10], intensity=1.0, distance=50, angle=45, penumbra=0.5, decay=2);',
    description: 'Adds a cone-shaped spot light.',
    fields: [
      {
        name: 'color',
        label: 'Color (R,G,B)',
        type: 'vector3',
        default: [1, 1, 1],
        step: 0.1,
      },
      {
        name: 'position',
        label: 'Position (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 10],
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'number',
        default: 1.0,
        step: 0.1,
      },
      { name: 'angle', label: 'Angle', type: 'number', default: 45 },
    ],
    generate: (v) =>
      `light_spot(color=[${v.color.join(', ')}], position=[${v.position.join(', ')}], intensity=${v.intensity}, angle=${v.angle});`,
  },
};

export const SCENE: Record<string, ScadFeature> = {
  scene_material: {
    id: 'scene_material',
    label: 'Scene Material',
    code: 'scene_material(metalness = 0.0, roughness = 0.5);',
    description:
      'Sets scene-wide default material properties. Only specified fields override the UI; -1 (the default) defers to the UI value.',
    fields: [
      {
        name: 'metalness',
        label: 'Metalness',
        type: 'number',
        default: 0.0,
        min: 0,
        max: 1,
        step: 0.01,
      },
      {
        name: 'roughness',
        label: 'Roughness',
        type: 'number',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.01,
      },
      {
        name: 'env_map_intensity',
        label: 'Env Reflection',
        type: 'number',
        default: 0.1,
        min: 0,
        max: 1,
        step: 0.01,
      },
    ],
    generate: (v) =>
      `scene_material(metalness = ${v.metalness}, roughness = ${v.roughness}, env_map_intensity = ${v.env_map_intensity});`,
  },
  scene_default_lights: {
    id: 'scene_default_lights',
    label: 'Default Lights',
    code: 'scene_default_lights(ambient = 1.9, key = 1.61, fill = 0.5, rim = 0.16);',
    description:
      'Sets intensities for the built-in three-point rig. Omit a parameter (or pass -1) to keep the UI value.',
    fields: [
      {
        name: 'ambient',
        label: 'Ambient',
        type: 'number',
        default: 1.9,
        min: 0,
        max: 4,
        step: 0.05,
      },
      {
        name: 'key',
        label: 'Key',
        type: 'number',
        default: 1.61,
        min: 0,
        max: 3,
        step: 0.05,
      },
      {
        name: 'fill',
        label: 'Fill',
        type: 'number',
        default: 0.5,
        min: 0,
        max: 2,
        step: 0.05,
      },
      {
        name: 'rim',
        label: 'Rim',
        type: 'number',
        default: 0.16,
        min: 0,
        max: 2,
        step: 0.05,
      },
    ],
    generate: (v) =>
      `scene_default_lights(ambient = ${v.ambient}, key = ${v.key}, fill = ${v.fill}, rim = ${v.rim});`,
  },
  scene_shadows: {
    id: 'scene_shadows',
    label: 'Scene Shadows',
    code: 'scene_shadows(enabled = 1, soft = 1, intensity = 0.3, map_size = 2048);',
    description:
      'Controls the shadow renderer. enabled/soft accept 1 (on) or 0 (off). -1 defers to the UI.',
    fields: [
      {
        name: 'enabled',
        label: 'Enabled (0/1)',
        type: 'number',
        default: 1,
        min: 0,
        max: 1,
        step: 1,
      },
      {
        name: 'soft',
        label: 'Soft (0/1)',
        type: 'number',
        default: 1,
        min: 0,
        max: 1,
        step: 1,
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'number',
        default: 0.3,
        min: 0,
        max: 1,
        step: 0.01,
      },
      { name: 'map_size', label: 'Map Size', type: 'number', default: 2048 },
    ],
    generate: (v) =>
      `scene_shadows(enabled = ${v.enabled}, soft = ${v.soft}, intensity = ${v.intensity}, map_size = ${v.map_size});`,
  },
  scene_bloom: {
    id: 'scene_bloom',
    label: 'Scene Bloom',
    code: 'scene_bloom(enabled = 1, intensity = 1.0, threshold = 0.9, smoothing = 0.025);',
    description:
      'Controls the bloom (glow) post-processing effect. enabled accepts 1 (on) or 0 (off). -1 defers to the UI.',
    fields: [
      {
        name: 'enabled',
        label: 'Enabled (0/1)',
        type: 'number',
        default: 1,
        min: 0,
        max: 1,
        step: 1,
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'number',
        default: 1.0,
        min: 0,
        max: 5,
        step: 0.05,
      },
      {
        name: 'threshold',
        label: 'Threshold',
        type: 'number',
        default: 0.9,
        min: 0,
        max: 1,
        step: 0.01,
      },
      {
        name: 'smoothing',
        label: 'Smoothing',
        type: 'number',
        default: 0.025,
        min: 0,
        max: 1,
        step: 0.005,
      },
    ],
    generate: (v) =>
      `scene_bloom(enabled = ${v.enabled}, intensity = ${v.intensity}, threshold = ${v.threshold}, smoothing = ${v.smoothing});`,
  },
  scene_ssao: {
    id: 'scene_ssao',
    label: 'Scene SSAO',
    code: 'scene_ssao(enabled = 1, intensity = 4.0, radius = 2.0);',
    description:
      'Controls Screen Space Ambient Occlusion. enabled accepts 1 (on) or 0 (off). -1 defers to the UI.',
    fields: [
      {
        name: 'enabled',
        label: 'Enabled (0/1)',
        type: 'number',
        default: 1,
        min: 0,
        max: 1,
        step: 1,
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'number',
        default: 4.0,
        min: 0,
        max: 4,
        step: 0.1,
      },
      {
        name: 'radius',
        label: 'Radius',
        type: 'number',
        default: 2.0,
        min: 0,
        max: 2,
        step: 0.01,
      },
      {
        name: 'luminance_influence',
        label: 'Luminance Influence',
        type: 'number',
        default: 1.0,
        min: 0,
        max: 1,
        step: 0.01,
      },
    ],
    generate: (v) =>
      `scene_ssao(enabled = ${v.enabled}, intensity = ${v.intensity}, radius = ${v.radius}, luminance_influence = ${v.luminance_influence});`,
  },
  scene_background: {
    id: 'scene_background',
    label: 'Background Color',
    code: 'scene_background(color = [0.1, 0.1, 0.12]);',
    description:
      'Sets the 3D viewport background color from code. Overrides the color picker in the toolbar. Color is an RGB vector with values 0–1.',
    fields: [
      {
        name: 'r',
        label: 'Red',
        type: 'number',
        default: 0.1,
        min: 0,
        max: 1,
        step: 0.01,
      },
      {
        name: 'g',
        label: 'Green',
        type: 'number',
        default: 0.1,
        min: 0,
        max: 1,
        step: 0.01,
      },
      {
        name: 'b',
        label: 'Blue',
        type: 'number',
        default: 0.12,
        min: 0,
        max: 1,
        step: 0.01,
      },
    ],
    generate: (v) => `scene_background(color = [${v.r}, ${v.g}, ${v.b}]);`,
  },
};

export const CAMERAS: Record<string, ScadFeature> = {
  pip: {
    id: 'pip',
    label: 'PiP Camera',
    description:
      'Adds a picture-in-picture camera overlay to the viewer. The camera position and look-at target are fixed in 3D space and unaffected by the main camera. Up to 4 PiP cameras can be defined. Overlay position and size use normalized 0–1 viewport coordinates (0,0 = top-left).',
    fields: [
      {
        name: 'position',
        label: 'Camera Position (X,Y,Z)',
        type: 'vector3',
        default: [50, 50, 50],
      },
      {
        name: 'target',
        label: 'Look-at Target (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'fov',
        label: 'FOV (degrees)',
        type: 'number',
        default: 45,
        min: 1,
        max: 120,
        step: 1,
      },
      {
        name: 'x',
        label: 'Overlay X (0–1)',
        type: 'number',
        default: 0.02,
        min: 0,
        max: 0.9,
        step: 0.01,
      },
      {
        name: 'y',
        label: 'Overlay Y (0–1)',
        type: 'number',
        default: 0.02,
        min: 0,
        max: 0.9,
        step: 0.01,
      },
      {
        name: 'width',
        label: 'Width (0–1)',
        type: 'number',
        default: 0.3,
        min: 0.05,
        max: 0.9,
        step: 0.01,
      },
      {
        name: 'height',
        label: 'Height (0–1)',
        type: 'number',
        default: 0.25,
        min: 0.05,
        max: 0.9,
        step: 0.01,
      },
      {
        name: 'label',
        label: 'Label (optional)',
        type: 'string',
        default: '',
      },
    ],
    generate: (v) => {
      const label = v.label ? `,\n  label="${v.label}"` : '';
      return `camera_pip(\n  position=[${v.position.join(', ')}],\n  target=[${v.target.join(', ')}],\n  fov=${v.fov},\n  x=${v.x}, y=${v.y},\n  width=${v.width}, height=${v.height}${label}\n);`;
    },
  },
  pip_ortho: {
    id: 'pip_ortho',
    label: 'PiP Camera (Orthographic)',
    description:
      'Adds an orthographic picture-in-picture camera overlay. Useful for technical/engineering views without perspective distortion.',
    fields: [
      {
        name: 'position',
        label: 'Camera Position (X,Y,Z)',
        type: 'vector3',
        default: [100, 0, 0],
      },
      {
        name: 'target',
        label: 'Look-at Target (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'fov',
        label: 'Zoom (higher = more zoomed out)',
        type: 'number',
        default: 30,
        min: 1,
        max: 120,
        step: 1,
      },
      {
        name: 'x',
        label: 'Overlay X (0–1)',
        type: 'number',
        default: 0.68,
        min: 0,
        max: 0.9,
        step: 0.01,
      },
      {
        name: 'y',
        label: 'Overlay Y (0–1)',
        type: 'number',
        default: 0.02,
        min: 0,
        max: 0.9,
        step: 0.01,
      },
      {
        name: 'width',
        label: 'Width (0–1)',
        type: 'number',
        default: 0.3,
        min: 0.05,
        max: 0.9,
        step: 0.01,
      },
      {
        name: 'height',
        label: 'Height (0–1)',
        type: 'number',
        default: 0.25,
        min: 0.05,
        max: 0.9,
        step: 0.01,
      },
      {
        name: 'label',
        label: 'Label (optional)',
        type: 'string',
        default: '',
      },
    ],
    generate: (v) => {
      const label = v.label ? `,\n  label="${v.label}"` : '';
      return `camera_pip(\n  position=[${v.position.join(', ')}],\n  target=[${v.target.join(', ')}],\n  type="orthographic",\n  fov=${v.fov},\n  x=${v.x}, y=${v.y},\n  width=${v.width}, height=${v.height}${label}\n);`;
    },
  },
};

export const SCENE_GRIDS: Record<string, ScadFeature> = {
  plane_grid: {
    id: 'plane_grid',
    label: 'Plane Grid',
    description:
      'Adds a flat grid plane to the scene. Useful for showing scale and alignment. The grid is purely visual and does not affect geometry.',
    fields: [
      {
        name: 'size',
        label: 'Size',
        type: 'number',
        default: 200,
        min: 1,
        max: 2000,
        step: 10,
      },
      {
        name: 'position',
        label: 'Position (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'rotation',
        label: 'Rotation (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'cell_size',
        label: 'Cell Size',
        type: 'number',
        default: 1,
        min: 0.1,
        max: 100,
        step: 0.5,
      },
      {
        name: 'section_size',
        label: 'Section Size',
        type: 'number',
        default: 10,
        min: 1,
        max: 500,
        step: 1,
      },
      {
        name: 'opacity',
        label: 'Opacity',
        type: 'number',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.05,
      },
    ],
    generate: (v) =>
      `scene_grid(\n  type = 0,\n  size = ${v.size},\n  position = [${v.position.join(', ')}],\n  rotation = [${v.rotation.join(', ')}],\n  cell_size = ${v.cell_size},\n  section_size = ${v.section_size},\n  opacity = ${v.opacity}\n);`,
  },
  cube_wireframe: {
    id: 'cube_wireframe',
    label: 'Cube Wireframe',
    description:
      'Adds a wireframe cube to the scene. Useful for visualizing bounding boxes or spatial regions.',
    fields: [
      {
        name: 'size',
        label: 'Size',
        type: 'number',
        default: 100,
        min: 1,
        max: 2000,
        step: 10,
      },
      {
        name: 'position',
        label: 'Position (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'rotation',
        label: 'Rotation (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'color',
        label: 'Color (R,G,B)',
        type: 'vector3',
        default: [0.5, 0.5, 0.5],
        step: 0.05,
      },
      {
        name: 'opacity',
        label: 'Opacity',
        type: 'number',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.05,
      },
    ],
    generate: (v) =>
      `scene_grid(\n  type = 1,\n  size = ${v.size},\n  position = [${v.position.join(', ')}],\n  rotation = [${v.rotation.join(', ')}],\n  color = [${v.color.join(', ')}],\n  opacity = ${v.opacity}\n);`,
  },
  sphere_wireframe: {
    id: 'sphere_wireframe',
    label: 'Sphere Wireframe',
    description:
      'Adds a wireframe sphere to the scene. Useful for visualizing radial boundaries or clearance zones.',
    fields: [
      {
        name: 'size',
        label: 'Diameter',
        type: 'number',
        default: 100,
        min: 1,
        max: 2000,
        step: 10,
      },
      {
        name: 'position',
        label: 'Position (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'rotation',
        label: 'Rotation (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'color',
        label: 'Color (R,G,B)',
        type: 'vector3',
        default: [0.5, 0.5, 0.5],
        step: 0.05,
      },
      {
        name: 'opacity',
        label: 'Opacity',
        type: 'number',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.05,
      },
    ],
    generate: (v) =>
      `scene_grid(\n  type = 2,\n  size = ${v.size},\n  position = [${v.position.join(', ')}],\n  rotation = [${v.rotation.join(', ')}],\n  color = [${v.color.join(', ')}],\n  opacity = ${v.opacity}\n);`,
  },
};

export const EXTRUDE_OPS: Record<string, ScadFeature> = {
  linear_extrude: {
    id: 'linear_extrude',
    label: 'Linear Extrude',
    before: 'linear_extrude(height=10) ',
    after: '',
    wrap: true,
    description: 'Extrudes a 2D shape along the Z axis into 3D.',
    fields: [
      { name: 'height', label: 'Height', type: 'number', default: 10 },
      { name: 'twist', label: 'Twist', type: 'number', default: 0 },
      { name: 'slices', label: 'Slices', type: 'number', default: 20 },
      { name: 'scale', label: 'Scale', type: 'number', default: 1, step: 0.1 },
    ],
    generateWrap: (v) => ({
      before: `linear_extrude(height=${v.height}, twist=${v.twist}, slices=${v.slices}, scale=${v.scale})\n`,
      after: '',
    }),
    diagram: LinearExtrudeDiagram,
  },
  rotate_extrude: {
    id: 'rotate_extrude',
    label: 'Rotate Extrude',
    before: 'rotate_extrude(angle=360, $fn=30) ',
    after: '',
    wrap: true,
    description: 'Spins a 2D shape around the Z axis to form a solid.',
    fields: [
      { name: 'angle', label: 'Angle', type: 'number', default: 360 },
      { name: 'fn', label: 'Resolution ($fn)', type: 'number', default: 30 },
    ],
    generateWrap: (v) => ({
      before: `rotate_extrude(angle=${v.angle}, $fn=${v.fn})\n`,
      after: '',
    }),
    diagram: RotateExtrudeDiagram,
  },
};

// ... keep DEFAULT_SCAD_CODE and SNIPPETS unchanged below ...

export const CLONE_OPS: Record<string, ScadFeature> = {
  clone_1d: {
    id: 'clone_1d',
    label: '1D Clone (Line)',
    description: 'Clones elements repeatedly along an axis or vector.',
    wrap: true,
    fields: [
      { name: 'count', label: 'Count', type: 'number', default: 5, min: 1 },
      {
        name: 'spacing',
        label: 'Spacing (X,Y,Z)',
        type: 'vector3',
        default: [10, 0, 0],
      },
    ],
    generateWrap: (v) => ({
      before: `for (i = [0 : ${v.count} - 1]) {\n  translate([i * ${v.spacing[0]}, i * ${v.spacing[1]}, i * ${v.spacing[2]}])\n  `,
      after: `\n}`,
    }),
    diagram: Clone1dDiagram,
  },
  clone_2d: {
    id: 'clone_2d',
    label: '2D Clone (Grid)',
    description: 'Clones elements in a 2D grid matrix.',
    wrap: true,
    fields: [
      {
        name: 'count',
        label: 'Count (X,Y)',
        type: 'vector2',
        default: [3, 3],
        min: 1,
      },
      {
        name: 'spacing',
        label: 'Spacing (X,Y)',
        type: 'vector2',
        default: [10, 10],
      },
    ],
    generateWrap: (v) => ({
      before: `for (x = [0 : ${v.count[0]} - 1], y = [0 : ${v.count[1]} - 1]) {\n  translate([x * ${v.spacing[0]}, y * ${v.spacing[1]}, 0])\n  `,
      after: `\n}`,
    }),
    diagram: Clone2dDiagram,
  },
  clone_3d: {
    id: 'clone_3d',
    label: '3D Clone (Cube)',
    description: 'Clones elements in a 3D grid matrix.',
    wrap: true,
    fields: [
      {
        name: 'count',
        label: 'Count (X,Y,Z)',
        type: 'vector3',
        default: [3, 3, 3],
        min: 1,
      },
      {
        name: 'spacing',
        label: 'Spacing (X,Y,Z)',
        type: 'vector3',
        default: [10, 10, 10],
      },
    ],
    generateWrap: (v) => ({
      before: `for (x = [0 : ${v.count[0]} - 1], y = [0 : ${v.count[1]} - 1], z = [0 : ${v.count[2]} - 1]) {\n  translate([x * ${v.spacing[0]}, y * ${v.spacing[1]}, z * ${v.spacing[2]}])\n  `,
      after: `\n}`,
    }),
    diagram: Clone3dDiagram,
  },
};

// ---------------------------------------------------------------------------
// scene_camera — initial camera position / target / FOV
// ---------------------------------------------------------------------------
export const SCENE_CAMERA: Record<string, ScadFeature> = {
  scene_camera: {
    id: 'scene_camera',
    label: 'Scene Camera',
    description:
      'Sets the initial camera position, look-at target, field of view, and projection type when the model first loads. A one-shot initialisation — the viewer can still orbit freely afterwards.',
    fields: [
      {
        name: 'position',
        label: 'Camera Position (X,Y,Z)',
        type: 'vector3',
        default: [120, -80, 90],
      },
      {
        name: 'target',
        label: 'Look-at Target (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'fov',
        label: 'FOV (degrees)',
        type: 'number',
        default: 45,
        min: 1,
        max: 120,
        step: 1,
      },
    ],
    generate: (v) =>
      `scene_camera(\n  position = [${v.position.join(', ')}],\n  target   = [${v.target.join(', ')}],\n  fov      = ${v.fov}\n);`,
  },
};

// ---------------------------------------------------------------------------
// camera_path — intro camera path waypoints
// ---------------------------------------------------------------------------
export const CAMERA_PATH: Record<string, ScadFeature> = {
  camera_path: {
    id: 'camera_path',
    label: 'Camera Path Waypoint',
    description:
      'Adds a waypoint to the intro camera path. Waypoints play in source order once after the first compile — the camera animates through each position before settling on the last one where the user can interact. Suppresses fit-to-zoom automatically. Identical animation parameters to camera_pip().',
    fields: [
      {
        name: 'position',
        label: 'Camera Position (X,Y,Z)',
        type: 'vector3',
        default: [80, 60, 80],
      },
      {
        name: 'target',
        label: 'Look-at Target (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'fov',
        label: 'FOV (degrees)',
        type: 'number',
        default: 45,
        min: 1,
        max: 120,
        step: 1,
      },
      {
        name: 'duration',
        label: 'Duration (seconds)',
        type: 'number',
        default: 1.5,
        min: 0.1,
        max: 10,
        step: 0.1,
      },
    ],
    generate: (v) =>
      `camera_path(\n  position   = [${v.position.join(', ')}],\n  target     = [${v.target.join(', ')}],\n  fov        = ${v.fov},\n  transitions = "orbit",\n  easing     = "ease_in_out",\n  duration   = ${v.duration}\n);`,
  },
};

// ---------------------------------------------------------------------------
// svg_overlay — 2-D SVG overlay on top of the viewer
// ---------------------------------------------------------------------------
export const SVG_OVERLAY: Record<string, ScadFeature> = {
  svg_overlay: {
    id: 'svg_overlay',
    label: 'SVG Overlay',
    description:
      'Renders an uploaded SVG file as a 2-D HTML overlay on top of the viewer. Position and size use 0–1 viewport fractions (or pixels when > 1). Does not affect geometry or exports.',
    fields: [
      {
        name: 'file',
        label: 'SVG Filename',
        type: 'string',
        default: 'logo.svg',
      },
      {
        name: 'x',
        label: 'X position (0–1 or px)',
        type: 'number',
        default: 0.02,
        min: -1,
        max: 1,
        step: 0.01,
      },
      {
        name: 'y',
        label: 'Y position (0–1 or px)',
        type: 'number',
        default: 0.02,
        min: -1,
        max: 1,
        step: 0.01,
      },
      {
        name: 'width',
        label: 'Width (0–1 or px)',
        type: 'number',
        default: 0.2,
        min: 0.01,
        max: 1,
        step: 0.01,
      },
    ],
    generate: (v) =>
      `svg_overlay(\n  file  = "${v.file}",\n  x     = ${v.x},\n  y     = ${v.y},\n  width = ${v.width}\n);`,
  },
};

// ---------------------------------------------------------------------------
// Overlay & scene interaction features
// ---------------------------------------------------------------------------
export const INTERACTION_FEATURES: Record<string, ScadFeature> = {
  annotation: {
    id: 'annotation',
    label: 'Annotation',
    description:
      'Attaches a floating text label to child geometry in the 3D viewport. Does not affect geometry or exports.',
    wrap: true,
    fields: [
      {
        name: 'name',
        label: 'Label',
        type: 'string',
        default: 'Part name',
      },
      {
        name: 'description',
        label: 'Description',
        type: 'string',
        default: '',
      },
    ],
    generateWrap: (v) => {
      const desc = v.description ? `, description = "${v.description}"` : '';
      return {
        before: `annotation(name = "${v.name}"${desc})\n`,
        after: '',
      };
    },
  },
  interactive: {
    id: 'interactive',
    label: 'Interactive (click to set)',
    description:
      'Makes child geometry clickable. Clicking writes a new value to the named SCAD variable and triggers a recompile.',
    wrap: true,
    fields: [
      {
        name: 'variable_name',
        label: 'Variable Name',
        type: 'string',
        default: 'my_var',
      },
      {
        name: 'value',
        label: 'Value on click (leave blank for boolean toggle)',
        type: 'string',
        default: '',
      },
    ],
    generateWrap: (v) => {
      const val = v.value !== '' ? `, value = ${v.value}` : '';
      return {
        before: `interactive("${v.variable_name}"${val})\n`,
        after: '',
      };
    },
  },
  interactive_publish: {
    id: 'interactive_publish',
    label: 'Interactive Publish (pub/sub)',
    description:
      'Makes child geometry clickable. Clicking publishes a value to a pub/sub key so texture scripts react instantly — no recompile triggered.',
    wrap: true,
    fields: [
      {
        name: 'pub_key',
        label: 'Pub/Sub Key',
        type: 'string',
        default: 'btn_pressed',
      },
      {
        name: 'value',
        label: 'Value to publish (default: true)',
        type: 'string',
        default: '',
      },
    ],
    generateWrap: (v) => {
      const val = v.value !== '' ? `, value = ${v.value}` : '';
      return {
        before: `interactive_publish("${v.pub_key}"${val})\n`,
        after: '',
      };
    },
  },
};

// ---------------------------------------------------------------------------
// motion() — animate geometry in the preview
// ---------------------------------------------------------------------------
export const MOTION_FEATURES: Record<string, ScadFeature> = {
  motion_translate: {
    id: 'motion_translate',
    label: 'Motion: Translate (LFO)',
    description:
      'Animates child geometry along an axis using a waveform oscillator. Runs entirely in Three.js — no WASM recompile.',
    wrap: true,
    fields: [
      {
        name: 'axis',
        label: 'Axis (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 1],
      },
      {
        name: 'shape',
        label: 'Wave shape',
        type: 'string',
        default: 'sine',
      },
      {
        name: 'frequency',
        label: 'Frequency (Hz)',
        type: 'number',
        default: 1,
        min: 0.01,
        max: 10,
        step: 0.1,
      },
      {
        name: 'min',
        label: 'Min distance',
        type: 'number',
        default: -10,
        step: 1,
      },
      {
        name: 'max',
        label: 'Max distance',
        type: 'number',
        default: 10,
        step: 1,
      },
    ],
    generateWrap: (v) => ({
      before: `motion(\n  translate_axis = [${v.axis.join(', ')}],\n  translate_lfo  = "${v.shape}:${v.frequency}:0:${v.min}:${v.max}"\n)\n`,
      after: '',
    }),
  },
  motion_orbit: {
    id: 'motion_orbit',
    label: 'Motion: Orbit',
    description:
      'Animates child geometry revolving around a center point. Runs entirely in Three.js — no WASM recompile.',
    wrap: true,
    fields: [
      {
        name: 'radius',
        label: 'Orbit radius',
        type: 'number',
        default: 40,
        min: 0,
        step: 1,
      },
      {
        name: 'speed',
        label: 'Speed (rev/s)',
        type: 'number',
        default: 0.2,
        min: -10,
        max: 10,
        step: 0.05,
      },
      {
        name: 'center',
        label: 'Center (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'axis',
        label: 'Orbit axis (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 1],
      },
    ],
    generateWrap: (v) => ({
      before: `motion(\n  orbit_center = [${v.center.join(', ')}],\n  orbit_axis   = [${v.axis.join(', ')}],\n  orbit_radius = ${v.radius},\n  orbit_speed  = ${v.speed}\n)\n`,
      after: '',
    }),
  },
  motion_spin: {
    id: 'motion_spin',
    label: 'Motion: Spin',
    description:
      'Continuously rotates child geometry around a local axis. Runs entirely in Three.js — no WASM recompile.',
    wrap: true,
    fields: [
      {
        name: 'axis',
        label: 'Spin axis (X,Y,Z)',
        type: 'vector3',
        default: [0, 0, 1],
      },
      {
        name: 'speed',
        label: 'Speed (rev/s)',
        type: 'number',
        default: 0.5,
        min: -20,
        max: 20,
        step: 0.1,
      },
    ],
    generateWrap: (v) => ({
      before: `motion(\n  rotate_axis  = [${v.axis.join(', ')}],\n  rotate_speed = ${v.speed}\n)\n`,
      after: '',
    }),
  },
};

// ---------------------------------------------------------------------------
// Mesh clone — clone_ref / clone (render-time instancing)
// ---------------------------------------------------------------------------
export const MESH_CLONE: Record<string, ScadFeature> = {
  clone_ref: {
    id: 'clone_ref',
    label: 'Clone Source (clone_ref)',
    description:
      'Tags child geometry with a string key so that clone() can place render-time copies. The wrapped geometry compiles once and renders at its SCAD position.',
    wrap: true,
    fields: [
      {
        name: 'key',
        label: 'Clone key (unique name)',
        type: 'string',
        default: 'part',
      },
    ],
    generateWrap: (v) => ({
      before: `clone_ref("${v.key}")\n`,
      after: '',
    }),
  },
  clone: {
    id: 'clone',
    label: 'Clone Instance (clone)',
    description:
      'Places a render-time copy of a clone_ref() geometry at a given position, rotation, and scale. No extra WASM compilation.',
    fields: [
      {
        name: 'key',
        label: 'Clone key',
        type: 'string',
        default: 'part',
      },
      {
        name: 'position',
        label: 'Position (X,Y,Z)',
        type: 'vector3',
        default: [30, 0, 0],
      },
      {
        name: 'rotation',
        label: 'Rotation (X,Y,Z °)',
        type: 'vector3',
        default: [0, 0, 0],
      },
      {
        name: 'scale',
        label: 'Scale (X,Y,Z)',
        type: 'vector3',
        default: [1, 1, 1],
      },
    ],
    generate: (v) =>
      `clone("${v.key}",\n  position = [${v.position.join(', ')}],\n  rotation = [${v.rotation.join(', ')}],\n  scale    = [${v.scale.join(', ')}]\n);`,
  },
};

// ---------------------------------------------------------------------------
// material_def() — PBR material definition
// ---------------------------------------------------------------------------
export const MATERIAL_DEF: Record<string, ScadFeature> = {
  material_def: {
    id: 'material_def',
    label: 'Material Def (PBR)',
    description:
      'Returns a PBR material definition vector. Assign to a variable to get a material editor in the parameter panel, or pass directly to material().',
    fields: [
      {
        name: 'metalness',
        label: 'Metalness (0–1)',
        type: 'number',
        default: 0.0,
        min: 0,
        max: 1,
        step: 0.05,
      },
      {
        name: 'roughness',
        label: 'Roughness (0–1)',
        type: 'number',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.05,
      },
      {
        name: 'clearcoat',
        label: 'Clearcoat (0–1)',
        type: 'number',
        default: 0,
        min: 0,
        max: 1,
        step: 0.05,
      },
      {
        name: 'transmission',
        label: 'Transmission/Glass (0–1)',
        type: 'number',
        default: 0,
        min: 0,
        max: 1,
        step: 0.05,
      },
    ],
    generate: (v) =>
      `material_def(metalness = ${v.metalness}, roughness = ${v.roughness}, clearcoat = ${v.clearcoat}, transmission = ${v.transmission})`,
  },
};

// ---------------------------------------------------------------------------
// shader_material() — custom GLSL shader wrapper
// ---------------------------------------------------------------------------
export const SHADER_MATERIAL: Record<string, ScadFeature> = {
  shader_material: {
    id: 'shader_material',
    label: 'Shader Material (GLSL)',
    description:
      'Applies a custom GLSL shader script to child geometry. Upload a .js shader file to your project, then reference it here. The shader fully replaces the standard PBR material.',
    wrap: true,
    fields: [
      {
        name: 'filename',
        label: 'Shader script filename (.js)',
        type: 'string',
        default: 'shader.js',
      },
    ],
    generateWrap: (v) => ({
      before: `shader_material("${v.filename}")\n`,
      after: '',
    }),
  },
};

// ---------------------------------------------------------------------------
// js_svg() — JS-driven SVG geometry script
// ---------------------------------------------------------------------------
export const JS_SVG: Record<string, ScadFeature> = {
  js_svg: {
    id: 'js_svg',
    label: 'JS SVG Geometry Script',
    code: 'js_svg(props = ["filename", "script.js", "value", 0])',
    description:
      'Runs a JavaScript file before WASM compilation to generate an SVG, then imports it as 2D geometry. Supports smooth Bézier curves, data-driven outlines, and any SVG path syntax.',
    fields: [
      {
        name: 'filename',
        label: 'JS script filename',
        type: 'string',
        default: 'script.js',
      },
      {
        name: 'value',
        label: 'Per-instance value (loop variable or 0)',
        type: 'number',
        default: 0,
      },
    ],
    generate: (v) =>
      `js_svg(props = ["filename", "${v.filename}", "value", ${v.value}])`,
  },
};

export const DEFAULT_SCAD_CODE = `/* Demo Code */

// markdown:tip
// **Welcome!** This demo shows a parametric container. Use the tabs to explore
// shape dimensions, appearance options, and advanced settings.
// Try changing **Shape**, **Add Lid**, or **Add Engraving** below to see
// groups appear and disappear across tabs.

// Base shape of the container
shape = "box"; // [box, cylinder]

// Add a lid?
add_lid = true;

// Add side engraving? (box only)
add_engraving = true; // {shape == "box"}

camera_pip(
  position=[-3.47, 107.64, 199.45],
  target=[0, 20, 0],
  fov=25,
  ar="16:9",
  title="Preview",
  caption="Adjust shape and toggles to see changes",
  mode="grayscale"
);

/* [Shape: Dimensions] */

// Width (box only)
width = 88; // [5 .. [20:1:120] .. 150] {shape == "box"}

// Depth (box only) — safe range tracks width so the box stays sensible
depth = 45; // [5 .. [20:1:$width] .. $width] {shape == "box"}

// Diameter (cylinder only)
diameter = 55; // [20:1:120] {shape == "cylinder"}

// Height of the container
height = 40; // [10:1:100]

// Wall thickness
wall = 2.5; // [1:0.5:6]

// markdown:warning {wall < 2}
// Be careful with thin walls, they are harder to print!

// Corner radius (box only)
corner_radius = 5; // [0:0.5:15] {shape == "box"}

/* [Shape: Lid] {add_lid == true} */
// Lid attachment style
lid_style = "snap"; // [snap, friction, threaded]

// Thread pitch in mm (threaded lid only)
thread_pitch = 2; // [1, 1.5, 2, $wall] {lid_style == "threaded"}

// Lid height — can't meaningfully exceed the container height
lid_height = 5; // [1 .. [3:0.5:20] .. $height]

// Lid knob radius — naturally bounded by lid height
knob_radius = 5; // [$wall:0.5:$lid_height]

/* [Shape: Engraving] {shape == "box" && add_engraving == true} */
// Text to engrave
engrave_text = "MESHPIT";

// Engrave font
engrave_font = "Noto Sans:style=Bold"; // font

// Engrave depth
engrave_depth = 0.8; // [0.2:0.1:2]

// Engrave size
engrave_size = 6; // [3:0.5:14]

/* [Appearance: Colors] */

// Body color
// color
body_color = [0.486, 0.349, 0.192];

// Lid color
// color
lid_color = [0.192, 0.153, 0.153]; // {add_lid == true}

// Engraving color
// color
engrave_color = [1, 1, 1]; // {shape == "box" && add_engraving == true}

/* [Appearance: Materials] */
// Body material
body_mat = material_def(metalness=0.1, roughness=0.4);

// Lid material
lid_mat = material_def(metalness=0.05, roughness=0.25);

// Engraving material
engraving_mat = material_def(metalness=0.8, roughness=0);

/* [Advanced: Resolution] */
// Global resolution
$fn = 60; // [20, 60, 120, 240]

// --- parameters end ---

// ----- Helpers -----

module rounded_box(w, d, h, r, center=false) {
    rr = min(r, w/2 - 0.01, d/2 - 0.01);
    translate(center ? [0,0,0] : [w/2, d/2, h/2])
    minkowski() {
        cube([w - 2*rr, d - 2*rr, h], center=true);
        cylinder(r=rr, h=0.01, center=true);
    }
}

// ----- Body -----

is_box = (shape == "box");
body_w = is_box ? width  : diameter;
body_d = is_box ? depth  : diameter;

module body_shell() {
    difference() {
        if (is_box) {
            rounded_box(body_w, body_d, height, corner_radius, center=true);
        } else {
            cylinder(d=diameter, h=height, center=true);
        }
        // Hollow interior
        translate([0, 0, wall])
        if (is_box) {
            rounded_box(body_w - 2*wall, body_d - 2*wall, height, max(0, corner_radius - wall), center=true);
        } else {
            cylinder(d=diameter - 2*wall, h=height, center=true);
        }
    }
}

material(body_mat)
color(body_color)
translate([0, 0, height/2])
body_shell();

// ----- Lid -----

module lid_shape(h) {
    if (is_box) {
        rounded_box(body_w - 0.4, body_d - 0.4, h, max(0, corner_radius - 0.2), center=true);
    } else {
        cylinder(d=diameter - 0.4, h=h, center=true);
    }
}

module knob() {
    translate([0, 0, lid_height/2 + knob_radius * 0.4])
    sphere(r=knob_radius);
}

if (add_lid) {
    material(lid_mat)
    color(lid_color)
    translate([0, 0, height + lid_height/2 + 1])
    union() {
        lid_shape(lid_height);
        knob();
    }
}

// ----- Engraving -----
// Body is centered, so the camera-facing front is at -body_d/2.
// Translate to -side_y, rotate [-90,0,0] so linear_extrude goes outward in -Y.

if (add_engraving && shape == "box") {
    side_y = is_box ? body_d/2 : diameter/2;
    material(engraving_mat)
    color(engrave_color)
    translate([0, -side_y + 0.5, height * 0.5])
    rotate([-270, 0, 0])
    linear_extrude(engrave_depth)
    text(engrave_text, font=engrave_font, size=engrave_size, halign="center", valign="center");
}

// ----- Lights -----
light_directional(color=[0.8,0.9,1], position=[80, 60, 120], intensity=0.3);
light_directional(color=[1,0.85,0.7], position=[-60, -40, 80], intensity=0.2);

camera_pip(
  position=[0.5, 24.11, 107.22],
  target=[0, 20, 0],
  fov=45,
  x=0.02, y=0.02,
  width=0.2, height=0.15,
  transitions           = "orbit,zoom_out,roll,fov_pulse",
  orbit_turns           = 1.0,
  zoom_out_factor       = 2.0,
  roll_angle            = 360,
  fov_pulse_amount      = 25,
  fov_pulse_direction   = "out",
  easing                = "ease_in_out",
  duration              = 3.5,
);

camera_pip(
  position=[-58.75, 65.61, 26.83],
  target=[-17.71, 5.17, 10.16],
  fov=45,
  x=-0.24, y=0.02,
  width=0.2, height=0.15,
  transitions       = "arc,bounce",
  arc_height        = 50,
  bounce_amplitude  = 0.3,
  easing            = "ease_in_out",
  duration          = 1.8
);

camera_pip(
  position=[-0.14, 228.76, 52.85],
  target=[0, 26.49, 0.15],
  fov=45,
  x=-0.02, y=0.02,
  width=0.2, height=0.15,
  transitions = "orbit,zoom_out",
  orbit_turns = 1,
  easing      = "ease_in_out",
  duration    = 2.0,
  zoom_out_factor       = 2.0,
);`;

export const MODIFIER_OPS: Record<string, ScadFeature> = {
  debug: {
    id: 'debug',
    label: 'Debug / Highlight (#)',
    description: 'Renders the subtree in transparent red.',
    before: '#',
    after: '',
    wrap: true,
  },
  background: {
    id: 'background',
    label: 'Background (%)',
    description:
      'Renders the subtree in transparent gray and excludes it from booleans.',
    before: '%',
    after: '',
    wrap: true,
  },
  root: {
    id: 'root',
    label: 'Root / Show Only (!)',
    description: 'Ignores everything else and only renders this subtree.',
    before: '!',
    after: '',
    wrap: true,
  },
  disable: {
    id: 'disable',
    label: 'Disable (*)',
    description: 'Ignores this subtree entirely.',
    before: '*',
    after: '',
    wrap: true,
  },
  exclude_from_export: {
    id: 'exclude_from_export',
    label: 'Exclude from Export (~)',
    description:
      'Renders the subtree in the preview but excludes it from exported files.',
    before: '~',
    after: '',
    wrap: true,
  },
};

export const RENDER_SETTINGS: Record<string, ScadFeature> = {
  render_fn: {
    id: 'render_fn',
    label: 'Resolution ($fn)',
    description:
      'Sets the global number of fragments (resolution) for curved surfaces. Overrides $fa and $fs.',
    fields: [{ name: 'fn', label: '$fn', type: 'number', default: 50 }],
    generate: (v) => `$fn = ${v.fn};
`,
    diagram: FnDiagram,
  },
  render_fa: {
    id: 'render_fa',
    label: 'Min Angle ($fa)',
    description: 'Minimum angle (in degrees) for each fragment of a curve.',
    fields: [
      { name: 'fa', label: '$fa', type: 'number', default: 12, step: 0.1 },
    ],
    generate: (v) => `$fa = ${v.fa};
`,
    diagram: FnDiagram,
  },
  render_fs: {
    id: 'render_fs',
    label: 'Min Size ($fs)',
    description: 'Minimum size (in mm) for each fragment of a curve.',
    fields: [
      { name: 'fs', label: '$fs', type: 'number', default: 2, step: 0.1 },
    ],
    generate: (v) => `$fs = ${v.fs};
`,
    diagram: FnDiagram,
  },
};

export const LIBRARY_FEATURES: Record<string, ScadFeature[]> = {
  BOSL2: [
    {
      id: 'bosl2_cuboid',
      library: 'BOSL2',
      label: 'Cuboid (Rounded)',
      description: 'A 3D cuboid with configurable edge rounding.',
      fields: [
        {
          name: 'size',
          label: 'Size (X,Y,Z)',
          type: 'vector3',
          default: [10, 10, 10],
        },
        {
          name: 'rounding',
          label: 'Rounding',
          type: 'number',
          default: 1,
          step: 0.1,
        },
      ],
      generate: (
        v,
      ) => `cuboid([${v.size[0]}, ${v.size[1]}, ${v.size[2]}], rounding=${v.rounding});
`,
    },
    {
      id: 'bosl2_cyl',
      library: 'BOSL2',
      label: 'Cyl (Advanced)',
      description:
        'A powerful cylinder module that can have rounded edges or chamfers.',
      fields: [
        { name: 'r', label: 'Radius', type: 'number', default: 5 },
        { name: 'l', label: 'Length', type: 'number', default: 10 },
        {
          name: 'chamfer',
          label: 'Chamfer',
          type: 'number',
          default: 1,
          step: 0.1,
        },
      ],
      generate: (v) => `cyl(r=${v.r}, l=${v.l}, chamfer=${v.chamfer});
`,
    },
    {
      id: 'bosl2_stroke',
      library: 'BOSL2',
      label: 'Stroke',
      description: 'Sweeps a 2D shape along a 3D path.',
      code: 'stroke([[0,0], [10,10], [20,0]], width=2);\n',
    },
  ],
  MCAD: [
    {
      id: 'mcad_roundedBox',
      library: 'MCAD',
      label: 'Rounded Box',
      description: 'Creates a box with rounded edges.',
      fields: [
        {
          name: 'size',
          label: 'Size (X,Y,Z)',
          type: 'vector3',
          default: [10, 10, 10],
        },
        {
          name: 'radius',
          label: 'Radius',
          type: 'number',
          default: 1,
          step: 0.1,
        },
      ],
      generate: (
        v,
      ) => `roundedBox(size=[${v.size[0]}, ${v.size[1]}, ${v.size[2]}], radius=${v.radius}, sidesonly=false);
`,
    },
    {
      id: 'mcad_gear',
      library: 'MCAD',
      label: 'Involute Gear',
      description: 'Creates a 2D involute gear.',
      fields: [
        { name: 'teeth', label: 'Teeth', type: 'number', default: 15 },
        { name: 'pitch', label: 'Pitch Radius', type: 'number', default: 10 },
      ],
      generate: (
        v,
      ) => `gear(number_of_teeth=${v.teeth}, circular_pitch=${v.pitch});
`,
    },
    {
      id: 'mcad_stepper',
      library: 'MCAD',
      label: 'Stepper Motor',
      description: 'Generates a NEMA stepper motor placeholder.',
      code: 'motor(Nema17, size, dualAxis=false);\n',
    },
  ],
  BOSL: [
    {
      id: 'bosl_cuboid',
      library: 'BOSL',
      label: 'Cuboid',
      description: 'A 3D box with rounded corners.',
      fields: [
        {
          name: 'size',
          label: 'Size (X,Y,Z)',
          type: 'vector3',
          default: [10, 10, 10],
        },
        {
          name: 'fillet',
          label: 'Fillet',
          type: 'number',
          default: 1,
          step: 0.1,
        },
      ],
      generate: (
        v,
      ) => `cuboid([${v.size[0]}, ${v.size[1]}, ${v.size[2]}], fillet=${v.fillet});
`,
    },
    {
      id: 'bosl_cyl',
      library: 'BOSL',
      label: 'Cylinder',
      description: 'A cylinder with chamfered or rounded ends.',
      fields: [
        { name: 'r', label: 'Radius', type: 'number', default: 5 },
        { name: 'l', label: 'Length', type: 'number', default: 10 },
        {
          name: 'chamfer',
          label: 'Chamfer',
          type: 'number',
          default: 1,
          step: 0.1,
        },
      ],
      generate: (v) => `cyl(r=${v.r}, l=${v.l}, chamfer=${v.chamfer});
`,
    },
    {
      id: 'bosl_pie',
      library: 'BOSL',
      label: 'Pie Slice',
      description: 'A pie slice of a cylinder.',
      fields: [
        { name: 'ang', label: 'Angle', type: 'number', default: 45 },
        { name: 'r', label: 'Radius', type: 'number', default: 10 },
        { name: 'h', label: 'Height', type: 'number', default: 5 },
      ],
      generate: (v) => `pie_slice(ang=${v.ang}, r=${v.r}, h=${v.h});
`,
    },
  ],
  gridfinity: [
    {
      id: 'gridfinity_simple_bin',
      library: 'gridfinity',
      label: 'Simple Bin',
      description:
        'A Gridfinity bin with evenly divided compartments. Width/length in 42mm grid units, height in 7mm units.',
      fields: [
        {
          name: 'gridx',
          label: 'Width (grid units)',
          type: 'number',
          default: 3,
        },
        {
          name: 'gridy',
          label: 'Depth (grid units)',
          type: 'number',
          default: 2,
        },
        {
          name: 'gridz',
          label: 'Height (7mm units)',
          type: 'number',
          default: 6,
        },
        { name: 'divx', label: 'X Compartments', type: 'number', default: 3 },
        { name: 'divy', label: 'Y Compartments', type: 'number', default: 2 },
      ],
      generate: (v) =>
        `$fa = 4; $fs = 0.25;
bin = new_bin(
    grid_size = [${v.gridx}, ${v.gridy}],
    height_mm = height(${v.gridz}, 0)
);

bin_render(bin) {
    bin_subdivide(bin, [${v.divx}, ${v.divy}]) {
        cut_compartment_auto(cgs(), style_tab = 1, scoop = 1);
    }
}
`,
    },
    {
      id: 'gridfinity_custom_bin',
      library: 'gridfinity',
      label: 'Custom Compartments',
      description:
        'A Gridfinity bin with manually placed compartments using bin_translate() and compartment_cutter().',
      code: `$fa = 4; $fs = 0.25;

/* [Bin Size] */
gridx = 3; // [1:6]
gridy = 3; // [1:6]
gridz = 6; // [1:12]

bin = new_bin(
    grid_size = [gridx, gridy],
    height_mm = height(gridz, 0)
);

bin_render(bin) {
    // Left column: one tall compartment
    bin_translate(bin, [0, 0])
    compartment_cutter(cgs([1, gridy]), center_top = false);

    // Right area: equal grid of smaller compartments
    bin_translate(bin, [1, 0])
    bin_subdivide(bin, [gridx - 1, gridy]) {
        cut_compartment_auto(cgs(), style_tab = 1, scoop = 1);
    }
}
`,
    },
    {
      id: 'gridfinity_baseplate',
      library: 'gridfinity',
      label: 'Baseplate',
      description:
        'A Gridfinity baseplate. Uses gridfinity-rebuilt-baseplate.scad.',
      fields: [
        {
          name: 'gridx',
          label: 'Width (grid units)',
          type: 'number',
          default: 3,
        },
        {
          name: 'gridy',
          label: 'Depth (grid units)',
          type: 'number',
          default: 3,
        },
      ],
      generate: (v) =>
        `include <gridfinity/gridfinity-rebuilt-baseplate.scad>

/* [Baseplate] */
hole_options = bundle_hole_options(
    refined_hole = false,
    magnet_hole  = true,
    screw_hole   = false,
    crush_ribs   = true,
    chamfer      = true,
    supportless  = false
);

color("tomato")
gridfinityBaseplate(
    [${v.gridx}, ${v.gridy}],
    l_grid,
    [0, 0],
    style_plate = 3,
    hole_options = hole_options,
    style_hole = 0
);
`,
    },
  ],
  LEGO: [
    {
      id: 'lego_brick',
      library: 'LEGO',
      label: 'Lego Brick',
      description: 'A standard Lego brick.',
      fields: [
        { name: 'width', label: 'Width (studs)', type: 'number', default: 2 },
        { name: 'length', label: 'Length (studs)', type: 'number', default: 4 },
        {
          name: 'height',
          label: 'Height',
          type: 'number',
          default: 1,
          step: 0.5,
        },
      ],
      generate: (
        v,
      ) => `block(width=${v.width}, length=${v.length}, height=${v.height});
`,
    },
    {
      id: 'lego_plate',
      library: 'LEGO',
      label: 'Lego Plate',
      description: 'A 1/3 height Lego plate.',
      fields: [
        { name: 'width', label: 'Width (studs)', type: 'number', default: 2 },
        { name: 'length', label: 'Length (studs)', type: 'number', default: 4 },
      ],
      generate: (
        v,
      ) => `block(width=${v.width}, length=${v.length}, height=1/3, type="plate");
`,
    },
    {
      id: 'lego_tile',
      library: 'LEGO',
      label: 'Lego Tile',
      description: 'A flat Lego tile without studs.',
      fields: [
        { name: 'width', label: 'Width (studs)', type: 'number', default: 2 },
        { name: 'length', label: 'Length (studs)', type: 'number', default: 4 },
      ],
      generate: (
        v,
      ) => `block(width=${v.width}, length=${v.length}, height=1/3, type="tile");
`,
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// PARAMETER FEATURES
// Each entry generates a complete OpenSCAD variable declaration line when
// inserted via the Parameters dropdown in the ScadToolbar.
// ─────────────────────────────────────────────────────────────────────────────

export const PARAMETER_FEATURES: Record<string, ScadFeature> = {
  // ── Native OpenSCAD ────────────────────────────────────────────────────────

  number_slider: {
    id: 'number_slider',
    label: 'Number Slider',
    description:
      'A bare numeric variable becomes a slider. The default value determines the slider range heuristically.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'my_param' },
      { name: 'value', label: 'Default Value', type: 'number', default: 10 },
    ],
    generate: (v) => `${v.name} = ${v.value};`,
  },

  number_slider_range: {
    id: 'number_slider_range',
    label: 'Slider with Range',
    description:
      'Adds a `// [min:max]` comment to constrain the slider range.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'my_param' },
      { name: 'value', label: 'Default Value', type: 'number', default: 10 },
      { name: 'min', label: 'Min', type: 'number', default: 0 },
      { name: 'max', label: 'Max', type: 'number', default: 100 },
    ],
    generate: (v) => `${v.name} = ${v.value}; // [${v.min}:${v.max}]`,
  },

  number_slider_step: {
    id: 'number_slider_step',
    label: 'Slider with Step',
    description:
      'Adds a `// [min:step:max]` comment to set the slider range and step increment.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'my_param' },
      { name: 'value', label: 'Default Value', type: 'number', default: 10 },
      { name: 'min', label: 'Min', type: 'number', default: 0 },
      { name: 'step', label: 'Step', type: 'number', default: 1, step: 0.1 },
      { name: 'max', label: 'Max', type: 'number', default: 100 },
    ],
    generate: (v) => `${v.name} = ${v.value}; // [${v.min}:${v.step}:${v.max}]`,
  },

  number_slider_extended_range: {
    id: 'number_slider_extended_range',
    label: 'Slider with Extended Range',
    description:
      'Extended range syntax with an outer "unsafe" boundary and an inner safe range. The slider is constrained to the safe range but the user can type values up to the outer limits.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'my_param' },
      { name: 'value', label: 'Default Value', type: 'number', default: 10 },
      { name: 'outerMin', label: 'Outer Min', type: 'number', default: -200 },
      { name: 'safeMin', label: 'Safe Min', type: 'number', default: 0 },
      { name: 'step', label: 'Step', type: 'number', default: 1, step: 0.1 },
      { name: 'safeMax', label: 'Safe Max', type: 'number', default: 100 },
      { name: 'outerMax', label: 'Outer Max', type: 'number', default: 200 },
    ],
    generate: (v) =>
      `${v.name} = ${v.value}; // [${v.outerMin} .. [${v.safeMin}:${v.step}:${v.safeMax}] .. ${v.outerMax}]`,
  },

  boolean_toggle: {
    id: 'boolean_toggle',
    label: 'Boolean Toggle',
    description:
      '`true`/`false` variables become a toggle switch in the parameter panel.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'show_part' },
      { name: 'value', label: 'Default Value', type: 'boolean', default: true },
    ],
    generate: (v) => `${v.name} = ${v.value};`,
  },

  string_input: {
    id: 'string_input',
    label: 'Text Input',
    description:
      'Quoted string variables with no options become a text input field.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'label_text' },
      { name: 'value', label: 'Default Value', type: 'string', default: 'Hello World' },
    ],
    generate: (v) => `${v.name} = "${v.value}";`,
  },

  number_dropdown: {
    id: 'number_dropdown',
    label: 'Number Dropdown',
    description:
      'Add `[opt1, opt2, ...]` to a number parameter for a toggle group or dropdown.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'sides' },
      { name: 'value', label: 'Default Value', type: 'number', default: 6 },
      { name: 'opt1', label: 'Option 1', type: 'number', default: 3 },
      { name: 'opt2', label: 'Option 2', type: 'number', default: 4 },
      { name: 'opt3', label: 'Option 3', type: 'number', default: 6 },
      { name: 'opt4', label: 'Option 4', type: 'number', default: 8 },
    ],
    generate: (v) =>
      `${v.name} = ${v.value}; // [${v.opt1}, ${v.opt2}, ${v.opt3}, ${v.opt4}]`,
  },

  labelled_dropdown: {
    id: 'labelled_dropdown',
    label: 'Labelled Dropdown',
    description:
      'Use `value=Label` pairs to give human-readable names to numeric options.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'quality' },
      { name: 'value', label: 'Default Value', type: 'number', default: 32 },
      { name: 'val1', label: 'Value 1', type: 'number', default: 16 },
      { name: 'label1', label: 'Label 1', type: 'string', default: 'Draft' },
      { name: 'val2', label: 'Value 2', type: 'number', default: 32 },
      { name: 'label2', label: 'Label 2', type: 'string', default: 'Normal' },
      { name: 'val3', label: 'Value 3', type: 'number', default: 64 },
      { name: 'label3', label: 'Label 3', type: 'string', default: 'Fine' },
    ],
    generate: (v) =>
      `${v.name} = ${v.value}; // ${v.val1}=${v.label1}, ${v.val2}=${v.label2}, ${v.val3}=${v.label3}`,
  },

  string_dropdown: {
    id: 'string_dropdown',
    label: 'String Dropdown',
    description:
      'Add `[opt1, opt2, ...]` to a string parameter for a toggle group or dropdown.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'style' },
      { name: 'value', label: 'Default Value', type: 'string', default: 'round' },
      { name: 'opt1', label: 'Option 1', type: 'string', default: 'round' },
      { name: 'opt2', label: 'Option 2', type: 'string', default: 'square' },
      { name: 'opt3', label: 'Option 3', type: 'string', default: 'hex' },
    ],
    generate: (v) =>
      `${v.name} = "${v.value}"; // [${v.opt1}, ${v.opt2}, ${v.opt3}]`,
  },

  color_picker: {
    id: 'color_picker',
    label: 'Color Picker',
    description:
      'A `[R, G, B]` array whose variable name contains "color" renders as a full color picker.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'body_color' },
      {
        name: 'rgb',
        label: 'Default Color (R,G,B)',
        type: 'vector3',
        default: [0.2, 0.6, 1.0],
        step: 0.05,
      },
    ],
    generate: (v) =>
      `${v.name} = [${v.rgb.map((c: number) => +c.toFixed(3)).join(', ')}];`,
  },

  color_picker_alpha: {
    id: 'color_picker_alpha',
    label: 'Color Picker with Alpha',
    description:
      'A `[R, G, B, A]` array adds an opacity slider below the color picker.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'glass_color' },
      {
        name: 'rgba',
        label: 'Default Color (R,G,B,A)',
        type: 'vector4',
        default: [0.8, 0.9, 1.0, 0.4],
        step: 0.05,
      },
    ],
    generate: (v) =>
      `${v.name} = [${v.rgba.map((c: number) => +c.toFixed(3)).join(', ')}];`,
  },

  vector_sliders: {
    id: 'vector_sliders',
    label: 'Vector Sliders',
    description:
      'A numeric array with a `[label1, label2, ...]` comment becomes labelled sliders.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'offset' },
      {
        name: 'values',
        label: 'Default (X,Y,Z)',
        type: 'vector3',
        default: [10, 5, 0],
      },
      { name: 'lx', label: 'X Label', type: 'string', default: 'x' },
      { name: 'ly', label: 'Y Label', type: 'string', default: 'y' },
      { name: 'lz', label: 'Z Label', type: 'string', default: 'z' },
    ],
    generate: (v) =>
      `${v.name} = [${v.values.join(', ')}]; // [${v.lx}, ${v.ly}, ${v.lz}]`,
  },

  range_input: {
    id: 'range_input',
    label: 'Range Input',
    description:
      'An OpenSCAD range literal `[start:step:end]` becomes Start / Step / End number fields.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'helix_range' },
      { name: 'start', label: 'Start', type: 'number', default: 0 },
      { name: 'step', label: 'Step', type: 'number', default: 5, step: 0.1 },
      { name: 'end', label: 'End', type: 'number', default: 360 },
    ],
    generate: (v) => `${v.name} = [${v.start}:${v.step}:${v.end}];`,
  },

  parameter_groups: {
    id: 'parameter_groups',
    label: 'Parameter Groups',
    description:
      'Use `/* [Group Name] */` comments to organize parameters into collapsible accordion sections.',
    fields: [
      { name: 'groupName', label: 'Group Name', type: 'string', default: 'Dimensions' },
    ],
    generate: (v) => `/* [${v.groupName}] */`,
  },

  tab_groups: {
    id: 'tab_groups',
    label: 'Tab Groups',
    description:
      'Use `/* [Tab: Group] */` to organize groups under named tabs in the parameter panel.',
    fields: [
      { name: 'tabName', label: 'Tab Name', type: 'string', default: 'Design' },
      { name: 'groupName', label: 'Group Name', type: 'string', default: 'Shape' },
    ],
    generate: (v) => `/* [${v.tabName}: ${v.groupName}] */`,
  },

  stop_parsing: {
    id: 'stop_parsing',
    label: 'Stop Parsing',
    description:
      'Inserts `// --- parameters end ---` to stop the parser — variables below this line are ignored by the parameter panel.',
    code: '// --- parameters end ---',
  },

  // ── Meshpit Extensions ─────────────────────────────────────────────────────

  angle_dial: {
    id: 'angle_dial',
    label: 'Angle Dial',
    description:
      'Add a degree symbol `°` to the range comment to get a circular arc dial widget.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'rotation' },
      { name: 'value', label: 'Default Value', type: 'number', default: 0 },
      { name: 'min', label: 'Min (°)', type: 'number', default: -180 },
      { name: 'max', label: 'Max (°)', type: 'number', default: 180 },
    ],
    generate: (v) => `${v.name} = ${v.value}; // [${v.min}°:${v.max}°]`,
  },

  stepper: {
    id: 'stepper',
    label: 'Stepper',
    description:
      'Use `<val:Label, val:Label>` syntax to create prev/next arrow buttons for stepping through named values.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'resolution' },
      { name: 'value', label: 'Default Value', type: 'number', default: 32 },
      { name: 'val1', label: 'Value 1', type: 'number', default: 16 },
      { name: 'label1', label: 'Label 1', type: 'string', default: 'Draft' },
      { name: 'val2', label: 'Value 2', type: 'number', default: 32 },
      { name: 'label2', label: 'Label 2', type: 'string', default: 'Normal' },
      { name: 'val3', label: 'Value 3', type: 'number', default: 64 },
      { name: 'label3', label: 'Label 3', type: 'string', default: 'Fine' },
    ],
    generate: (v) =>
      `${v.name} = ${v.value}; // <${v.val1}:${v.label1}, ${v.val2}:${v.label2}, ${v.val3}:${v.label3}>`,
  },

  xy_pad: {
    id: 'xy_pad',
    label: 'XY Pad',
    description:
      'A two-element array with two range specs `[min:max, min:max]` becomes a draggable 2D canvas.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'position' },
      { name: 'defaultX', label: 'Default X', type: 'number', default: 0 },
      { name: 'defaultY', label: 'Default Y', type: 'number', default: 0 },
      { name: 'xMin', label: 'X Min', type: 'number', default: -50 },
      { name: 'xMax', label: 'X Max', type: 'number', default: 50 },
      { name: 'yMin', label: 'Y Min', type: 'number', default: -50 },
      { name: 'yMax', label: 'Y Max', type: 'number', default: 50 },
    ],
    generate: (v) =>
      `${v.name} = [${v.defaultX}, ${v.defaultY}]; // [${v.xMin}:${v.xMax}, ${v.yMin}:${v.yMax}]`,
  },

  xyz_pad: {
    id: 'xyz_pad',
    label: 'XYZ Pad',
    description:
      'A three-element array with three range specs becomes an XY canvas plus a Z slider.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'position' },
      { name: 'defaultX', label: 'Default X', type: 'number', default: 0 },
      { name: 'defaultY', label: 'Default Y', type: 'number', default: 0 },
      { name: 'defaultZ', label: 'Default Z', type: 'number', default: 0 },
      { name: 'xMin', label: 'X Min', type: 'number', default: -50 },
      { name: 'xMax', label: 'X Max', type: 'number', default: 50 },
      { name: 'yMin', label: 'Y Min', type: 'number', default: -50 },
      { name: 'yMax', label: 'Y Max', type: 'number', default: 50 },
      { name: 'zMin', label: 'Z Min', type: 'number', default: 0 },
      { name: 'zMax', label: 'Z Max', type: 'number', default: 100 },
    ],
    generate: (v) =>
      `${v.name} = [${v.defaultX}, ${v.defaultY}, ${v.defaultZ}]; // [${v.xMin}:${v.xMax}, ${v.yMin}:${v.yMax}, ${v.zMin}:${v.zMax}]`,
  },

  color_swatches: {
    id: 'color_swatches',
    label: 'Color Swatches',
    description:
      'A color parameter with a `[#hex1, #hex2, ...]` options list renders as clickable color swatches.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'accent_color' },
      { name: 'value', label: 'Default Color', type: 'string', default: '#3b82f6' },
      { name: 'swatch1', label: 'Swatch 1', type: 'string', default: '#ef4444' },
      { name: 'swatch2', label: 'Swatch 2', type: 'string', default: '#3b82f6' },
      { name: 'swatch3', label: 'Swatch 3', type: 'string', default: '#22c55e' },
      { name: 'swatch4', label: 'Swatch 4', type: 'string', default: '#f59e0b' },
    ],
    generate: (v) =>
      `${v.name} = "${v.value}"; // [${v.swatch1}, ${v.swatch2}, ${v.swatch3}, ${v.swatch4}]`,
  },

  font_picker: {
    id: 'font_picker',
    label: 'Font Picker',
    description:
      'A string variable whose name contains "font" renders as a font browser showing all OpenSCAD fonts.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'label_font' },
      { name: 'value', label: 'Default Font', type: 'string', default: 'Liberation Sans' },
    ],
    generate: (v) => `${v.name} = "${v.value}";`,
  },

  file_upload: {
    id: 'file_upload',
    label: 'File Upload',
    description:
      'Precede a string variable with `// upload` to render a file picker button for attaching external files.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'svg_file' },
      { name: 'value', label: 'Default Filename', type: 'string', default: 'logo.svg' },
    ],
    generate: (v) => `// upload\n${v.name} = "${v.value}";`,
  },

  markdown_block: {
    id: 'markdown_block',
    label: 'Markdown Block',
    description:
      'Embeds a rich-text callout in the parameter panel. Variants: tip, note, warning, idea, error.',
    fields: [
      {
        name: 'variant',
        label: 'Variant',
        type: 'select',
        default: 'tip',
        options: [
          { value: 'tip', label: 'Tip' },
          { value: 'note', label: 'Note' },
          { value: 'warning', label: 'Warning' },
          { value: 'idea', label: 'Idea' },
          { value: 'error', label: 'Error' },
        ],
      },
      {
        name: 'text',
        label: 'Text',
        type: 'string',
        default: 'Use **bold** or _italic_ text in your callouts.',
      },
    ],
    generate: (v) => `// markdown:${v.variant}\n// ${v.text}`,
  },

  grid_input: {
    id: 'grid_input',
    label: 'Grid Input',
    description:
      'A 2D array with a `// grid: CxR [opt1, opt2, ...]` comment renders as a clickable grid.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'layout' },
      { name: 'cols', label: 'Columns', type: 'number', default: 3, min: 1 },
      { name: 'rows', label: 'Rows', type: 'number', default: 2, min: 1 },
      { name: 'options', label: 'Options (comma-separated)', type: 'string', default: 'empty, filled' },
    ],
    generate: (v) => {
      const opts = String(v.options).split(',').map((s: string) => s.trim()).filter(Boolean);
      const firstOpt = opts[0] ?? 'empty';
      // Build a flat row-major 2D array defaulting all cells to firstOpt
      const row = Array(Number(v.cols)).fill(`"${firstOpt}"`);
      const rows = Array(Number(v.rows)).fill(`[${row.join(',')}]`);
      return `${v.name} = [${rows.join(',')}]; // grid: ${v.cols}x${v.rows} [${opts.join(', ')}]`;
    },
  },

  stack_input: {
    id: 'stack_input',
    label: 'Stack Input',
    description:
      'A 1D array with a `// stack: min:max [opt1, opt2, ...]` comment renders as a dynamic reorderable list.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'layout' },
      { name: 'minLen', label: 'Min Length', type: 'number', default: 1, min: 0 },
      { name: 'maxLen', label: 'Max Length', type: 'number', default: 10, min: 1 },
      { name: 'options', label: 'Options (comma-separated)', type: 'string', default: 'item_a, item_b, empty' },
    ],
    generate: (v) => {
      const opts = String(v.options).split(',').map((s: string) => s.trim()).filter(Boolean);
      const firstOpt = opts[0] ?? 'item';
      return `${v.name} = ["${firstOpt}"]; // stack: ${v.minLen}:${v.maxLen} [${opts.join(', ')}]`;
    },
  },

  polygon_pad: {
    id: 'polygon_pad',
    label: 'Polygon Pad',
    description:
      'A nested array of `[x, y]` pairs becomes an interactive polygon editor where you can drag vertices on a 2D canvas.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'outline' },
      { name: 'numPoints', label: 'Number of Points', type: 'number', default: 4, min: 3, max: 16 },
      { name: 'size', label: 'Size (radius)', type: 'number', default: 30, min: 1 },
    ],
    generate: (v) => {
      const n = Math.max(3, Math.min(16, Math.round(Number(v.numPoints))));
      const r = Number(v.size);
      const points = Array.from({ length: n }, (_, i) => {
        const angle = (2 * Math.PI * i) / n - Math.PI / 2;
        const x = Math.round(r * Math.cos(angle));
        const y = Math.round(r * Math.sin(angle));
        return `[${x},${y}]`;
      });
      return `${v.name} = [${points.join(', ')}];`;
    },
  },

  svg_checkbox: {
    id: 'svg_checkbox',
    label: 'SVG Checkbox Group',
    description:
      'Creates a multi-select visual toggle using SVG path groups as buttons. Precede with `// svgcheckboxgroup`.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'parts' },
      { name: 'svgFile', label: 'SVG File', type: 'string', default: 'enclosure.svg' },
      { name: 'options', label: 'Group IDs (comma-separated)', type: 'string', default: 'base, case, lid' },
      { name: 'defaultSelected', label: 'Default Selection (comma-separated)', type: 'string', default: 'base, lid' },
    ],
    generate: (v) => {
      const opts = String(v.options).split(',').map((s: string) => s.trim()).filter(Boolean);
      const defaults = String(v.defaultSelected).split(',').map((s: string) => s.trim()).filter(Boolean);
      const defaultArr = defaults.map((d: string) => `"${d}"`).join(', ');
      return `// svgcheckboxgroup\n${v.name} = "${v.svgFile}"; // [${opts.join(', ')}]\n// default: [${defaultArr}]`;
    },
  },

  svg_radio: {
    id: 'svg_radio',
    label: 'SVG Radio Group',
    description:
      'Creates a single-select visual picker using SVG path groups. Precede with `// svgradiogroup`.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'variant' },
      { name: 'svgFile', label: 'SVG File', type: 'string', default: 'variants.svg' },
      { name: 'options', label: 'Group IDs (comma-separated)', type: 'string', default: 'simple, advanced, pro' },
      { name: 'value', label: 'Default Selection', type: 'string', default: 'simple' },
    ],
    generate: (v) => {
      const opts = String(v.options).split(',').map((s: string) => s.trim()).filter(Boolean);
      return `// svgradiogroup\n${v.name} = "${v.svgFile}"; // [${opts.join(', ')}]`;
    },
  },

  visibility_expr: {
    id: 'visibility_expr',
    label: 'Visibility Expression',
    description:
      'Append `{expr}` to a parameter comment to conditionally hide it based on another parameter\'s value.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'lid_height' },
      { name: 'value', label: 'Default Value', type: 'number', default: 8 },
      { name: 'min', label: 'Min', type: 'number', default: 3 },
      { name: 'max', label: 'Max', type: 'number', default: 20 },
      { name: 'expr', label: 'Visibility Expression', type: 'string', default: 'add_lid == true' },
    ],
    generate: (v) => `${v.name} = ${v.value}; // [${v.min}:${v.max}] {${v.expr}}`,
  },

  snapshots: {
    id: 'snapshots',
    label: 'Named Snapshots',
    description:
      'Defines named parameter presets. Each snapshot restores a set of parameter values with one click.',
    fields: [
      { name: 'snap1Name', label: 'Snapshot 1 Name', type: 'string', default: 'Compact' },
      { name: 'snap1Params', label: 'Snapshot 1 Params (p=v, ...)', type: 'string', default: 'width=40, height=25' },
      { name: 'snap2Name', label: 'Snapshot 2 Name', type: 'string', default: 'Standard' },
      { name: 'snap2Params', label: 'Snapshot 2 Params (p=v, ...)', type: 'string', default: 'width=80, height=40' },
    ],
    generate: (v) =>
      `/* [Snapshots]\n${v.snap1Name}: ${v.snap1Params}\n${v.snap2Name}: ${v.snap2Params}\n*/`,
  },

  lock_annotation: {
    id: 'lock_annotation',
    label: 'Lock Annotation',
    description:
      'Append `@lock` to a parameter comment to lock it by default — excluded from randomization until unlocked.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'width' },
      { name: 'value', label: 'Default Value', type: 'number', default: 10 },
      { name: 'min', label: 'Min', type: 'number', default: 0 },
      { name: 'max', label: 'Max', type: 'number', default: 100 },
    ],
    generate: (v) => `${v.name} = ${v.value}; // [${v.min}:${v.max}] @lock`,
  },

  pubsub_annotation: {
    id: 'pubsub_annotation',
    label: 'Pub/Sub Annotation',
    description:
      'Append `@pubsub` to connect a parameter to the inter-script pub/sub channel for real-time sharing with texture/shader scripts.',
    fields: [
      { name: 'name', label: 'Variable Name', type: 'string', default: 'hue' },
      { name: 'value', label: 'Default Value', type: 'number', default: 0.5, step: 0.01 },
      { name: 'min', label: 'Min', type: 'number', default: 0, step: 0.01 },
      { name: 'step', label: 'Step', type: 'number', default: 0.01, step: 0.001 },
      { name: 'max', label: 'Max', type: 'number', default: 1, step: 0.01 },
    ],
    generate: (v) => `${v.name} = ${v.value}; // [${v.min}:${v.step}:${v.max}] @pubsub`,
  },
};

export const SNIPPETS: Record<
  string,
  { label: string; code: string; id: string }
> = {
  module: {
    id: 'module',
    label: 'Module',
    code: 'module myModule() {\n  // Your code here\n}',
  },
  function: {
    id: 'function',
    label: 'Function',
    code: 'function myFunc(x) = x * 2;',
  },
  for_loop: {
    id: 'for_loop',
    label: 'For Loop',
    code: 'for(i = [0 : 1 : 10]) {\n  // Your code here\n}',
  },
  if_else: {
    id: 'if_else',
    label: 'If/Else',
    code: 'if (condition) {\n  // true case\n} else {\n  // false case\n}',
  },
  parameters: {
    id: 'parameters',
    label: 'Parameter Block',
    code: '/* [Parameter Group] */\n// Parameter description\nmy_param = 10; // min: 0, max: 100',
  },
  font_parameter: {
    id: 'font_parameter',
    label: 'Font Parameter',
    code: '// Font for the text\nfont = "Liberation Sans"; // font',
  },
  polygon: {
    id: 'polygon',
    label: 'Polygon',
    code: 'polygon(points=[[0,0],[10,0],[5,10]]);',
  },
  polyhedron: {
    id: 'polyhedron',
    label: 'Polyhedron',
    code: 'polyhedron(\n  points=[[0,0,0],[10,0,0],[10,10,0],[0,10,0],[5,5,10]],\n  faces=[[0,1,2,3],[0,1,4],[1,2,4],[2,3,4],[3,0,4]]\n);',
  },
};
